<script setup lang="ts">
/**
 * Badge — branded pill/tag for decorative moments (e.g. "RENTED", "Premium", "Launches in 5 days").
 *
 * Differs from `Chip` in that its color values are a **curated decorative palette**, not the
 * kit's semantic vocabulary. Use `Chip` for status-feedback shapes (primary/success/warning/danger).
 *
 * Sizes:
 *   sm  → 23px height — image overlays
 *   md  → 26px height — gallery / detail surfaces
 *   lg  → 28px height — premium / hero moments
 */
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'

export type BadgeColor = 'blue' | 'navy' | 'cobalt' | 'green' | 'teal' | 'amber' | 'warning'
export type BadgeSize = 'sm' | 'md' | 'lg'

interface Props {
  label: string
  /** PrimeIcons name without the `pi-` prefix (e.g. `"calendar"`). */
  icon?: PiIconName
  color?: BadgeColor
  size?: BadgeSize
  /** Apply a backdrop blur — useful when overlaid on an image. */
  blur?: boolean
}

withDefaults(defineProps<Props>(), {
  color: 'blue',
  size: 'md',
  blur: false,
})
</script>

<template>
  <Box
    flex
    align="center"
    :class="['badge', `badge--color-${color}`, `badge--size-${size}`, { 'badge--blur': blur }]"
  >
    <PiIcon v-if="icon" :name="icon" class="badge__icon" />
    <Text is="span" class="badge__label">{{ label }}</Text>
  </Box>
</template>

<style scoped>
.badge {
  gap: 4px;
  border-radius: 26px;
  white-space: nowrap;
  flex-shrink: 0;
  justify-content: center;
}

/* ── Sizes ── */
.badge--size-sm {
  height: 23px;
  padding: 0 8px;
}

.badge--size-md {
  height: 26px;
  padding: 0 10px;
}

.badge--size-lg {
  height: 28px;
  padding: 0 10px;
  border-radius: 100px;
}

/* ── Blur overlay (for badges over images) ── */
.badge--blur {
  backdrop-filter: blur(2px);
}

/* ── Color variants ── */
.badge--color-blue {
  background: var(--p-sliceal-badge-blue);
}
.badge--color-navy {
  background: var(--p-sliceal-badge-navy);
}
.badge--color-cobalt {
  background: var(--p-sliceal-badge-cobalt);
}
.badge--color-green {
  background: var(--p-sliceal-badge-green);
}
.badge--color-teal {
  background: var(--p-sliceal-badge-teal);
}
.badge--color-amber {
  background: var(--p-sliceal-badge-amber);
}
.badge--color-warning {
  background: var(--p-sliceal-badge-amber-soft);
  border-radius: 8px;
}

/* ── Icon ── */
.badge__icon {
  font-size: 10px;
  flex-shrink: 0;
}

/* Default: white icon */
.badge--color-blue .badge__icon,
.badge--color-navy .badge__icon,
.badge--color-cobalt .badge__icon,
.badge--color-green .badge__icon,
.badge--color-teal .badge__icon,
.badge--color-amber .badge__icon {
  color: var(--p-primary-contrast-color);
}

.badge--color-warning .badge__icon {
  color: var(--p-sliceal-badge-amber-text);
}

.badge--size-sm .badge__icon {
  font-size: 13px;
}

.badge--size-md .badge__icon {
  font-size: 12px;
}

.badge--size-lg .badge__icon {
  font-size: 14px;
}

/* ── Label ── */
.badge__label {
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.04em;
}

/* Default: white label */
.badge--color-blue .badge__label,
.badge--color-navy .badge__label,
.badge--color-cobalt .badge__label,
.badge--color-green .badge__label,
.badge--color-teal .badge__label,
.badge--color-amber .badge__label {
  color: var(--p-primary-contrast-color);
}

.badge--color-warning .badge__label {
  color: var(--p-sliceal-badge-amber-text);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
}
</style>
